import { ConsumptionGranularity, type Meter } from "@eisox/api/core-api";
import dayjs from "@eisox/dayjs";
import { z } from "@eisox/zod";

import { EMPTY_VALUE } from "~/constants/display.constant";
import { toDayRangeIn, type DayRange } from "~/helpers/day-range.helper";
import { m } from "~/paraglide/messages";

import {
  COMPARISONS,
  DEFAULT_PERIOD_PRESET,
  DEGREE_DAYS_GRANULARITY,
  GRANULARITIES,
  PERIOD_PRESETS,
  WEEK_DAYS,
} from "../consumption.constant";
import {
  Comparison,
  type AppliedComparison,
  type CalendarSpan,
  type ChartView,
  type ComparedBuilding,
  type ComparedRange,
  type ConsumptionPeriod,
} from "../types/consumption.type";
import { objectivePercentSchema, type ConsumptionSearch } from "./consumption-search.helper";
import { supportsDegreeDays } from "./energy.helper";
import {
  availableGranularities,
  bucketCount,
  formatBucket,
  isReadable,
  pickGranularity,
  shiftRange,
  spanRange,
} from "./period.helper";

type ComparisonOptions = Pick<ConsumptionSearch, "compare" | "degreeDays">;

const NO_OPTION: ComparisonOptions = { compare: Comparison.NONE, degreeDays: false };

const comparisonShift = (
  comparison: Comparison,
  { range, granularity }: ConsumptionPeriod,
): CalendarSpan | undefined => {
  const { years } = COMPARISONS[comparison];
  if (years) return { amount: -years, unit: "year" };
  if (comparison !== Comparison.PREVIOUS_PERIOD) return undefined;
  const { calendarUnit } = GRANULARITIES[granularity];
  if (calendarUnit === "day")
    return { amount: -Math.ceil(bucketCount(range, ConsumptionGranularity.DAY) / WEEK_DAYS) * WEEK_DAYS, unit: "day" };
  return { amount: -bucketCount(range, granularity), unit: calendarUnit };
};

const comparedRange = (
  comparison: Comparison,
  period: ConsumptionPeriod,
  { referencePeriod, timezone }: ComparedBuilding,
): ComparedRange | undefined => {
  if (comparison === Comparison.NONE) return undefined;
  const shift = comparisonShift(comparison, period);
  if (shift) return { range: shiftRange(period.range, shift), shift };
  return referencePeriod && { range: toDayRangeIn(referencePeriod, timezone) };
};

const overlapsPeriod = (
  { range: compared, shift }: ComparedRange,
  range: DayRange,
  granularity: ConsumptionGranularity,
) => {
  const slot = (day: string) => dayjs.utc(day).format(GRANULARITIES[granularity].slotFormat);
  return shift !== undefined && slot(compared.to) >= slot(range.from);
};

const comparisonGranularities = (
  range: DayRange,
  { compare, degreeDays }: ComparisonOptions,
  building: ComparedBuilding,
  today: string,
): ConsumptionGranularity[] =>
  availableGranularities(range, today).filter(granularity => {
    if (!(COMPARISONS[compare].granularities?.includes(granularity) ?? true)) return false;
    if (degreeDays && granularity !== DEGREE_DAYS_GRANULARITY) return false;
    const compared = comparedRange(compare, { range, granularity }, building);
    return (
      !compared || (isReadable(compared.range, granularity, today) && !overlapsPeriod(compared, range, granularity))
    );
  });

const resolveChartView = (
  search: ConsumptionSearch,
  today: string,
  building: ComparedBuilding,
  meter: Pick<Meter, "energyType">,
): ChartView => {
  const presetOrDefault = search.preset ?? DEFAULT_PERIOD_PRESET;
  const range = search.range ?? spanRange(PERIOD_PRESETS[presetOrDefault].span, today);
  const preset = search.range ? undefined : presetOrDefault;
  const requested: ComparisonOptions = {
    compare: search.compare === Comparison.REFERENCE && !building.referencePeriod ? Comparison.NONE : search.compare,
    degreeDays: search.degreeDays && supportsDegreeDays(meter) && Boolean(building.weatherStationId),
  };
  const compatible = comparisonGranularities(range, requested, building, today);
  const options = compatible.length > 0 ? requested : NO_OPTION;
  const granularities = compatible.length > 0 ? compatible : availableGranularities(range, today);
  const period = {
    range,
    preset,
    granularity: pickGranularity(search.granularity, range, granularities, preset),
  };
  const compared = comparedRange(options.compare, period, building);
  return {
    period,
    granularities,
    degreeDays: options.degreeDays,
    comparison: compared && {
      ...compared,
      comparison: options.compare,
      objective: COMPARISONS[options.compare].objective ? search.objective : undefined,
    },
  };
};

const comparisonFormSchema = ({ range, granularity }: ConsumptionPeriod, building: ComparedBuilding, today: string) =>
  z
    .object({
      compare: z.enum(Comparison),
      degreeDays: z.boolean(),
      objective: objectivePercentSchema.or(z.undefined()),
    })
    .superRefine((options, context) => {
      const report = (path: keyof ComparisonOptions, message: string) =>
        context.addIssue({ code: "custom", path: [path], message });
      if (options.compare === Comparison.REFERENCE && !building.referencePeriod) {
        report("compare", m.consumption_comparison_missing_reference());
      }
      if (options.degreeDays && !building.weatherStationId) {
        report("degreeDays", m.consumption_comparison_missing_weather_station());
      }
      if (comparisonGranularities(range, options, building, today).length > 0) return;
      const compared = comparedRange(options.compare, { range, granularity }, building);
      if (compared && overlapsPeriod(compared, range, granularity)) {
        report("compare", m.consumption_comparison_overlaps_period());
        return;
      }
      report(options.degreeDays ? "degreeDays" : "compare", m.consumption_comparison_unavailable());
    });

const comparisonSearchPatch = (
  { compare, degreeDays, objective }: z.input<ReturnType<typeof comparisonFormSchema>>,
  { range, granularity }: ConsumptionPeriod,
  building: ComparedBuilding,
  today: string,
): Partial<ConsumptionSearch> => ({
  compare,
  degreeDays,
  objective: COMPARISONS[compare].objective ? objective : undefined,
  ...(!comparisonGranularities(range, { compare, degreeDays }, building, today).includes(granularity) && {
    granularity: undefined,
  }),
});

const appliedOptions = ({ comparison, degreeDays }: ChartView): ComparisonOptions => ({
  compare: comparison?.comparison ?? Comparison.NONE,
  degreeDays,
});

const formatComparedBucket = (
  { range, shift }: AppliedComparison,
  start: string,
  format: string,
  timezone: string,
): string => {
  if (shift) return formatBucket(start, format, timezone, shift);
  const bucket = dayjs(start).tz(timezone);
  const from = dayjs(range.from);
  const to = dayjs(range.to);
  const firstYear = from.year() + Number(bucket.month() < from.month());
  const lastYear = to.year() - Number(bucket.month() > to.month());
  if (firstYear > lastYear) return EMPTY_VALUE;
  return m.consumption_reference_years({ years: firstYear === lastYear ? firstYear : `${firstYear}–${lastYear}` });
};

export {
  appliedOptions,
  comparisonFormSchema,
  comparisonGranularities,
  comparisonSearchPatch,
  formatComparedBucket,
  resolveChartView,
};
